import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Optional,
  Output,
  Renderer2,
  Self,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { NgControl } from '@angular/forms';

import { ComponentVariant, Variants } from '../../common/variant/variant';
import { IconName } from '../../icons-container/icons.type';
import { RtTextInput } from '../../utils/rt-input';

@Component({
  selector: 'rt-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.less'],
  host: {
    '[class.disable-invalid-styles]': 'disableInvalidStyles',
    '[class.disabled]': 'disabled',
    '[class.invalid]': 'invalid',
    '[class.has-right-padding]': 'hasRightPadding',
    '[class.has-lr-padding]': 'hasLeftRightPadding',
    '[class.has-lr-padding-small]': 'hasLeftRightPaddingSmall',
    '[class.has-clear-button]': 'hasClearButton',
    '[class.has-remove-button]': 'isClearButtonAlwaysVisible',
    '[class.has-no-shadow]': 'hasNoShadow',
    '[style.width]': 'width',
    '[style.max-width]': 'maxWidth ? maxWidth : null',
    '[class.has-red-placeholder]': 'hasRedPlaceholder',
    '[class.has-no-spinners]': 'hasNoSpinners',
    '[class.with-icon]': '!!icon',
    '[class.icon-red-on-hover]': '!!icon && iconRedOnHover',
    '[class.icon-light]': '!!icon && iconLight',
    '[class.has-white-background]': 'hasWhiteBackground',
    '[class.phone-padding-left]': 'phonePaddingLeft',
    '[class]': 'variant',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: RtTextInput,
      // eslint-disable-next-line @angular-eslint/no-forward-ref
      useExisting: forwardRef(() => InputComponent),
    },
  ],
})
export class InputComponent extends RtTextInput {
  public static readonly VARIANTS: Variants = [
    'rounded',
    [
      'rounded',
      'underlined',
      'alt',
      'large',
      'small',
      'has-dropdown',
      'select-input',
      'no-grow',
      'small-text',
      'plain',
    ],
  ];
  @ComponentVariant(InputComponent.VARIANTS)
  @Input()
  public variant: string;
  @Input() public type: string = 'text';
  @Input() public placeholder: string;
  @Input() public maxLength: number;
  @Input() public tabIndex: number | undefined;
  @Input() public width: string;
  @Input() public maxWidth: string | null = null;
  @Input() public autoFocus: boolean = false;
  @Input() public dropdown: boolean = false; // TODO rename to "appendDropdownIcon"
  @Input() public dropdownIconHasMargin: boolean = true;
  @Input() public invalid: boolean;
  @Input() public readOnly: boolean = false;
  @Input() public hasRightPadding: boolean = false;
  @Input() public hasLeftRightPadding: boolean = false;
  @Input() public hasLeftRightPaddingSmall: boolean = false;
  @Input() public disableInvalidStyles: boolean = false;
  @Input() public hasClearButton: boolean = false;
  @Input() public isClearButtonAlwaysVisible: boolean = false;
  @Input() public hasNoShadow: boolean = false;
  @Input() public hasRedPlaceholder: boolean = false;
  @Input() public hasNoSpinners: boolean = false;
  @Input() public icon: IconName | null = null;
  @Input() public iconRedOnHover: boolean = false;
  @Input() public iconLight: boolean = false;
  @Input() public loading: boolean = false;
  @Input() public hasWhiteBackground: boolean = false;
  @Input() public focusOnClear: boolean = true;
  @Input() public phonePaddingLeft: boolean = false;

  @Output() public iconClick: EventEmitter<void> = new EventEmitter();
  @Output() public reset: EventEmitter<void> = new EventEmitter();

  @ViewChild('clearButton', { static: false, read: ElementRef }) public clearButton: ElementRef;
  @ViewChild('input', { static: true }) protected input: ElementRef;

  constructor(
    elementRef: ElementRef,
    renderer2: Renderer2,
    @Optional()
    @Self()
    ngControl: NgControl,
    cdRef: ChangeDetectorRef,
  ) {
    super(elementRef, renderer2, ngControl, cdRef);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.value) {
      this.setValue(this.value);
    }

    if (changes.disabled) {
      this.setDisabledState(this.disabled);
    }

    if (changes.autoFocus) {
      if (this.autoFocus) {
        this.setFocus();
      }
    }
  }

  public ngOnInit(): void {
    if (this.autoFocus) {
      this.setFocus();
    }
  }

  public setFocus(): void {
    if (this.input.nativeElement) {
      this.input.nativeElement.focus();
    }
  }

  public setBlur(): void {
    if (this.input.nativeElement) {
      this.input.nativeElement.blur();
    }
  }

  public setInputFocus(): void {
    if (this.input && this.focusOnClear) {
      this.setFocus();
    }
  }

  public mouseOverClearButton(): void {
    this.renderer2.addClass(this.elementRef.nativeElement, 'hover');
  }

  public mouseOutOfClearButton(): void {
    this.renderer2.removeClass(this.elementRef.nativeElement, 'hover');
  }

  public isRemoveButtonVisible(): boolean {
    return (
      ((this.value && this.hasClearButton) || this.isClearButtonAlwaysVisible) && !this.loading
    );
  }

  public onRemoveButtonCLick(): void {
    this.valueChange.emit('');
    this.reset.emit();
    this.setInputFocus();
    this.mouseOutOfClearButton();
  }

  protected setValue(value: string | null): void {
    this.value = value;
    if (this.input.nativeElement.value !== value) {
      this.input.nativeElement.value = value || '';
    }
    // @ts-ignore
    if (!this.cdRef['destroyed']) {
      this.cdRef.detectChanges();
    }
  }
}
